// src/components/shapeTakeoff/ShapeList_takeoff.js
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as ScvIcon } from '../../svg/csv-svgrepo-com.svg';
import { ReactComponent as SettingsIcon } from '../../svg/settings.svg';

import {
  getDisplayMeasurement,
  getDisplayAreaMeasurement,
  resolveShapeScaleProps,
} from '../Replan/utils/drawingUtils';
import { getShapeMeasurementData } from '../Replan/utils/measurementUtils';
import { TOOLS } from '../Replan/constants/drawingTools';
import { METRIC_SCALES, IMPERIAL_SCALES } from '../Replan/constants/scales';
import { create, all } from 'mathjs';
import { useHistory } from '../Replan/hooks/useHistory';
import { resolveAllParams } from '../ShapeList/paramUtils';
import { saveAssemblyFromShape } from '../ShapeList/assemblyUtils';

import AssemblyManager from '../AssemblyManager/AssemblyManager';
// ExportModal removed - now using direct CSV export
import DraggableShapeListItem_takeoff from './DraggableShapeListItem_takeoff.js'; // Import the new component
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragOverlay,
  pointerWithin,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DroppableGroup_takeoff from './DroppableGroup_takeoff.js';
import { useTranslation } from 'react-i18next';

// Initialize mathjs
const math = create(all);

// Define calculated param keys that should be recomputed per shape
const CALCULATED_PARAM_KEYS = [
  'Total Length',
  'Total Area',
  'Diameter',
];

// Performance: Maximum items to render before showing virtualization warning
const MAX_VISIBLE_ITEMS = 200;

// Built-in grouping key for grouping by pageInfo
const GROUP_BY_PAGE_KEY = '__page__';
const getGroupingLabel = (key, t) => {
  if (key === GROUP_BY_PAGE_KEY) return t('inventory.page_label');
  const map = {
    Assembly: t('inventory.assembly_label'),
    Color: t('inventory.color_label'),
    Reference: t('inventory.reference_label'),
    type: t('inventory.type_label'),
  };
  return map[key] || key;
};

// Items per page options
const ITEMS_PER_PAGE_OPTIONS = [50, 100, 200];

const ShapeList_takeoff = ({
  projectName,
  shapes,
  selectedShapeIds,
  onShapeSelect,
  onUpdateShape,
  onReorderShapes,
  currentScaleProps,
  dpi,
  imageSize,
  authUser,
  firebase,
  storageKeyPrefix = '',
  versions = [],
  selectedVersions = [],
  onVersionsChange,
  loadingVersionShapes = false,
  isPending = false,
  embedded = false,
  displayUnitSystem,
}) => {
  // Use useHistory for undo/redo functionality
  const {
    state: internalShapes,
    setState: setInternalShapes,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useHistory(shapes, { limit: 100, debounceMs: 800 });

  // Track which shapes have been modified (for optimized saving)
  const [modifiedShapeIds, setModifiedShapeIds] = useState(new Set());

  // Sync internal shapes when prop changes from parent
  useEffect(() => {
    // Only sync and clear if the shape IDs actually changed (not just updates to existing shapes)
    const incomingIds = shapes
      .map((s) => s.id)
      .sort()
      .join(',');
    const currentIds = internalShapes
      .map((s) => s.id)
      .sort()
      .join(',');

    if (incomingIds !== currentIds) {
      // Shape set changed (added/removed shapes), sync and clear modified tracking
      setInternalShapes(shapes, true);
      setModifiedShapeIds(new Set());
    }
    // If only shape content changed, don't sync (we have our own modifications)
  }, [shapes, internalShapes, setInternalShapes]);

  // Use internalShapes instead of shapes prop for all internal operations
  const workingShapes = internalShapes;

  // Build all scales for per-shape scale resolution
  const allScales = useMemo(() => {
    return [...METRIC_SCALES, ...IMPERIAL_SCALES];
  }, []);

  // Memoized measurement calculation to avoid recomputing on every render
  const getMeasurementDataForShape = useCallback(
    (shape) => {
      const ctx = shape?.measurementContext;
      const baseScaleProps = (ctx && ctx.currentScaleProps) ? ctx.currentScaleProps : currentScaleProps;
      const shapeScaleProps = resolveShapeScaleProps(shape, baseScaleProps, allScales);
      if (ctx && ctx.currentScaleProps && ctx.dpi) {
        return getShapeMeasurementData(
          shape,
          shapeScaleProps,
          ctx.dpi,
          ctx.imageSize || imageSize,
          displayUnitSystem || ctx.displayUnitSystem,
        );
      }
      return getShapeMeasurementData(
        shape,
        shapeScaleProps,
        dpi,
        imageSize,
        displayUnitSystem,
      );
    },
    [currentScaleProps, dpi, imageSize, displayUnitSystem, allScales],
  );

  // --- dnd-kit Sensors ---
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor),
  );

  // --- Local Storage Keys ---
  const EXPANDED_SHAPES_KEY = `${storageKeyPrefix}replan_expandedShapes`;
  const ACTIVE_DETAIL_VIEW_KEY = `${storageKeyPrefix}replan_activeDetailView`;
  const GROUPING_KEY = `${storageKeyPrefix}replan_groupingKey`;
  const STACK_KEY = `${storageKeyPrefix}replan_stackIdentical`; // Key for local storage
  const CURRENT_PAGE_KEY = `${storageKeyPrefix}replan_currentPage`;
  const ITEMS_PER_PAGE_KEY = `${storageKeyPrefix}replan_itemsPerPage`;
  const SEPARATE_BY_PAGE_KEY = `${storageKeyPrefix}replan_separateByPage`;

  // --- State Initialization ---
  const [expandedShapes, setExpandedShapes] = useState(() => {
    const stored = localStorage.getItem(EXPANDED_SHAPES_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const [activeDetailView, setActiveDetailView] = useState(() => {
    return 'params';
    // Keep for future use if we re-enable detail views, but for now default to 'Params' and ignore stored value since we removed the toggle
    // return localStorage.getItem(ACTIVE_DETAIL_VIEW_KEY) || 'Params';
  });

  // --- Pagination State ---
  const [currentPage, setCurrentPage] = useState(() => {
    const stored = localStorage.getItem(CURRENT_PAGE_KEY);
    return stored ? parseInt(stored, 10) : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const stored = localStorage.getItem(ITEMS_PER_PAGE_KEY);
    const defaultItemsPerPage = Math.max(...ITEMS_PER_PAGE_OPTIONS);
    return stored ? parseInt(stored, 10) : defaultItemsPerPage;
  });

  const { t } = useTranslation();

  // --- Assembly Save Status State ---
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [loadStatus, setLoadStatus] = useState('idle'); // 'idle', 'loading', 'loaded', 'error'
  const [projectAssemblies, setProjectAssemblies] = useState([]);
  const [groupingKey, setGroupingKey] = useState(() => {
    const saved = localStorage.getItem(GROUPING_KEY);
    return saved !== null ? saved : GROUP_BY_PAGE_KEY;
  }); // State for the selected grouping key
  const [collapsedGroups, setCollapsedGroups] = useState(new Set()); // State for collapsed groups
  const [stackIdentical, setStackIdentical] = useState(() => {
    const stored = localStorage.getItem(STACK_KEY);
    return stored ? JSON.parse(stored) : false;
  }); // State for stacking identical items
  // const [showSelectedOnly, setShowSelectedOnly] = useState(false); // REPLACED -- Cleaned up
  const [separateByPage, setSeparateByPage] = useState(() => {
    const stored = localStorage.getItem(SEPARATE_BY_PAGE_KEY);
    return stored ? JSON.parse(stored) : true;
  }); // Default true: separate stacks by page

  const [assemblyManagerOpen, setAssemblyManagerOpen] =
    useState(false);
  // ExportModal removed - now using direct CSV export

  // --- Version selector dropdown state ---
  const [isVersionDropdownOpen, setIsVersionDropdownOpen] =
    useState(false);
  const [versionDropdownPos, setVersionDropdownPos] = useState({
    top: 0,
    left: 0,
  });
  const versionTriggerRef = useRef(null);
  const versionDropdownMenuRef = useRef(null);
  const globalCheckboxRef = useRef(null);

  const onOpenAssemblyManager = () => {
    setAssemblyManagerOpen(true);
  };

  const handleApplyAssembly = (assemblyParams) => {
    if (!selectedShapeIds || selectedShapeIds.length === 0) {
      alert(t('inventory.please_select_shapes'));
      return;
    }
    const updatedShapes = workingShapes.map((shape) => {
      if (selectedShapeIds.includes(shape.id)) {
        const updatedParams = {
          ...(shape?.params || {}),
          ...(assemblyParams || {}),
        };
        // Notify parent of individual updates
        if (onUpdateShape) {
          onUpdateShape(shape.id, { params: updatedParams });
        }
        return { ...shape, params: updatedParams };
      }
      return shape;
    });

    // Track all modified shapes
    setModifiedShapeIds((prev) => {
      const newSet = new Set(prev);
      selectedShapeIds.forEach((id) => newSet.add(id));
      return newSet;
    });

    setInternalShapes(updatedShapes);
    setAssemblyManagerOpen(false);
  };

  const handleDeleteAssembly = async (assemblyId) => {
    if (!authUser || !firebase) {
      console.error('Not authenticated or Firebase not initialized.');
      return;
    }

    // Use organization from shapes if available, otherwise fallback to null (which firebase wrapper defaults to 'projects')
    const organization = shapes[0]?.pageInfo?.organization || organizationFromShapes;

    try {
      if (
        firebase.assemblyDoc &&
        typeof firebase.assemblyDoc === 'function'
      ) {
        await firebase
          .assemblyDoc(organization, projectName, assemblyId)
          .delete();
      } else if (firebase.doDeleteAssembly) {
        await firebase.doDeleteAssembly(
          organization,
          projectName,
          assemblyId,
        );
      } else {
        console.warn(
          'No delete method available on firebase wrapper.',
        );
      }
      setProjectAssemblies((prev) =>
        prev.filter((a) => a.id !== assemblyId),

      );
    } catch (err) {
      console.error('Error deleting assembly:', err);
      alert(
        t('inventory.failed_delete_assembly', { msg: err.message }),
      );
    }
  };

  // --- Effects to Persist State ---
  useEffect(() => {
    localStorage.setItem(
      EXPANDED_SHAPES_KEY,
      JSON.stringify(Array.from(expandedShapes)),
    );
  }, [expandedShapes]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_DETAIL_VIEW_KEY, activeDetailView);
  }, [activeDetailView]);

  useEffect(() => {
    localStorage.setItem(CURRENT_PAGE_KEY, currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    localStorage.setItem(ITEMS_PER_PAGE_KEY, itemsPerPage.toString());
  }, [itemsPerPage]);

  useEffect(() => {
    localStorage.setItem(GROUPING_KEY, groupingKey);
  }, [groupingKey]);

  useEffect(() => {
    localStorage.setItem(STACK_KEY, JSON.stringify(stackIdentical));
  }, [stackIdentical]);

  useEffect(() => {
    localStorage.setItem(SEPARATE_BY_PAGE_KEY, JSON.stringify(separateByPage));
  }, [separateByPage]);

  // Derive organization from shapes so effects can react when shapes load
  const organizationFromShapes =
    shapes[0]?.pageInfo?.organization || null;

  useEffect(() => {
    if (
      authUser &&
      firebase &&
      projectName &&
      organizationFromShapes
    ) {
      const fetchAssemblies = async () => {
        try {
          const querySnapshot = await firebase.doGetProjectAssemblies(
            organizationFromShapes,
            projectName,
          );
          const assemblies = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProjectAssemblies(assemblies);
          console.log(assemblies, 'Fetched project assemblies');
        } catch (error) {
          console.error('Error fetching project assemblies:', error);
          setProjectAssemblies([]); // Clear on error
        }
      };
      fetchAssemblies();
    }
  }, [
    authUser,
    firebase,
    projectName,
    saveStatus,
    loadStatus,
    organizationFromShapes,
  ]);

  // Close version dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger =
        versionTriggerRef.current &&
        versionTriggerRef.current.contains(e.target);
      const inMenu =
        versionDropdownMenuRef.current &&
        versionDropdownMenuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) {
        setIsVersionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () =>
      document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleVersionDropdown = useCallback(() => {
    setIsVersionDropdownOpen((prev) => {
      if (!prev && versionTriggerRef.current) {
        const rect =
          versionTriggerRef.current.getBoundingClientRect();
        setVersionDropdownPos({
          top: rect.bottom + 4,
          left: rect.left,
        });
      }
      return !prev;
    });
  }, []);

  // Handler for save functionality - saves shapes to Firebase
  const handleSave = useCallback(async () => {
    if (!firebase || !projectName) {
      console.warn('handleSave: Missing firebase or projectName');
      return;
    }

    // Filter to only modified shapes
    const modifiedShapes = workingShapes.filter((shape) =>
      modifiedShapeIds.has(shape.id),
    );

    if (modifiedShapes.length === 0) {
      console.log('No modified shapes to save');
      return;
    }

    console.log(
      `Saving ${modifiedShapes.length} modified shapes (out of ${workingShapes.length} total) to Firebase...`,
    );
    setSaveStatus('saving');

    try {
      // Group shapes by version and page
      const shapesByVersionAndPage = new Map();

      // For each modified shape, we need to save the entire page it belongs to
      const pagesToSave = new Set();
      modifiedShapes.forEach((shape) => {
        const version =
          shape.pageInfo?.version ||
          shape.measurementContext?.version;
        const pageIndex =
          shape.pageInfo?.pageIndex ??
          shape.pageInfo?.index ??
          shape.measurementContext?.pageIndex;
        if (version && pageIndex !== undefined) {
          pagesToSave.add(`${version}:${pageIndex}`);
        }
      });

      // Now collect ALL shapes for each page that needs saving
      workingShapes.forEach((shape) => {
        const version =
          shape.pageInfo?.version ||
          shape.measurementContext?.version;
        const pageIndex =
          shape.pageInfo?.pageIndex ??
          shape.pageInfo?.index ??
          shape.measurementContext?.pageIndex;

        if (!version || pageIndex === undefined) {
          return;
        }

        const key = `${version}:${pageIndex}`;

        // Only include pages that have modified shapes
        if (!pagesToSave.has(key)) {
          return;
        }
        if (!shapesByVersionAndPage.has(key)) {
          shapesByVersionAndPage.set(key, {
            version,
            pageIndex,
            shapes: [],
            measurementContext: shape.measurementContext,
            organization: shape.pageInfo?.organization,
          });
        }
        shapesByVersionAndPage.get(key).shapes.push(shape);
      });

      // Save each page's shapes
      for (const [key, data] of shapesByVersionAndPage) {
        const {
          version,
          pageIndex,
          shapes: pageShapes,
          measurementContext,
          organization,
        } = data;

        if (!organization) {
          console.warn(
            `No organization for page ${version}/${pageIndex}, skipping save`,
          );
          continue;
        }

        // Deduplicate shapes by ID
        const uniqueShapes = [];
        const seenIds = new Set();
        pageShapes.forEach((shape) => {
          if (!seenIds.has(shape.id)) {
            seenIds.add(shape.id);
            uniqueShapes.push(shape);
          }
        });

        // Sanitize shapes for Firebase (convert holes array format)
        const sanitizedShapes = uniqueShapes.map((shape) => {
          if (
            shape.holes &&
            Array.isArray(shape.holes) &&
            shape.holes.length > 0 &&
            Array.isArray(shape.holes[0])
          ) {
            return {
              ...shape,
              holes: shape.holes.map((holePoints) => ({
                points: holePoints,
              })),
            };
          }
          return shape;
        });

        // Prepare page data
        const pageData = {
          shapes: sanitizedShapes,
          measurementSystem:
            measurementContext?.currentScaleProps?.system || 'metric',
          selectedScaleKey:
            measurementContext?.currentScaleProps?.key || 'default',
          referenceWidth:
            measurementContext?.imageSize?.width ||
            imageSize?.width ||
            0,
          referenceHeight:
            measurementContext?.imageSize?.height ||
            imageSize?.height ||
            0,
        };

        console.log(
          `Saving page ${version}/${pageIndex} with ${sanitizedShapes.length} shapes`,
        );
        await firebase.doSavePageData(
          organization,
          projectName,
          version,
          pageIndex,
          pageData,
        );
      }

      // Also update parent state
      if (onUpdateShape) {
        workingShapes.forEach((shape) => {
          onUpdateShape(shape.id, shape);
        });
      }

      // Clear modified tracking after successful save
      setModifiedShapeIds(new Set());

      setSaveStatus('saved');
      console.log('Save complete!');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      console.error('Error saving shapes:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [
    firebase,
    projectName,
    workingShapes,
    imageSize,
    onUpdateShape,
  ]);

  // Keyboard shortcut for Save (Ctrl+S)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore shortcuts when typing in input fields
      const targetTagName = e.target.tagName.toLowerCase();
      const targetType = (e.target.type || '').toLowerCase();
      if (
        (targetTagName === 'input' && targetType !== 'checkbox') ||
        targetTagName === 'textarea' ||
        targetTagName === 'select'
      ) {
        return;
      }

      const key = e.key.toLowerCase();

      // Ctrl+S for Save
      if ((e.ctrlKey || e.metaKey) && key === 's') {
        e.preventDefault();
        handleSave();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSave]);

  // --- Global Selection Logic ---
  const allShapeIds = useMemo(() => workingShapes.map((s) => s.id), [workingShapes]);
  const isGlobalAllSelected =
    allShapeIds.length > 0 &&
    allShapeIds.every((id) => selectedShapeIds.includes(id));
  const isGlobalPartiallySelected =
    selectedShapeIds.length > 0 && !isGlobalAllSelected;

  useEffect(() => {
    if (globalCheckboxRef.current) {
      globalCheckboxRef.current.indeterminate = isGlobalPartiallySelected;
    }
  }, [isGlobalPartiallySelected]);

  const handleGlobalSelect = (e) => {
    const shouldSelectAll = !!e.target.checked;
    onShapeSelect(allShapeIds, shouldSelectAll);
  };

  // --- Local fallback ordering when parent doesn't implement onReorderShapes
  const [localOrder, setLocalOrder] = useState(() =>
    workingShapes.map((s) => s.id),
  );
  // Keep localOrder in sync when shapes list changes (e.g. shapes added/removed)
  useEffect(() => {
    // If shape set changed or localOrder is empty, re-init to incoming shapes order
    const incomingIds = workingShapes.map((s) => s.id);
    if (
      !localOrder ||
      localOrder.length !== incomingIds.length ||
      !incomingIds.every((id) => localOrder.includes(id))
    ) {
      setLocalOrder(incomingIds);
    }
  }, [workingShapes]);

  // Build an ordered shapes array using localOrder when available
  const orderedShapes = useMemo(() => {
    if (localOrder && localOrder.length === workingShapes.length) {
      return localOrder
        .map((id) => workingShapes.find((s) => s.id === id))
        .filter(Boolean);
    }
    return workingShapes;
  }, [workingShapes, localOrder]);

  // NEW: Visible (filtered) ordered shapes (used everywhere below)
  const visibleOrderedShapes = useMemo(() => {
    // Removed "showSelectedOnly" logic. Always return orderedShapes.
    return orderedShapes;
  }, [orderedShapes]);

  // --- Memoized Computations ---
  const allParamKeys = useMemo(() => {
    const keys = new Set();
    visibleOrderedShapes.forEach((shape) => {
      if (shape.params)
        Object.keys(shape.params).forEach((key) => keys.add(key));
    });
    return Array.from(keys).sort();
  }, [visibleOrderedShapes]);

  const hasPageInfo = useMemo(() => {
    return visibleOrderedShapes.some((s) => {
      const p = s?.pageInfo;
      return !!(
        p?.name ||
        p?.pageIndex !== undefined ||
        p?.index !== undefined
      );
    });
  }, [visibleOrderedShapes]);
  useEffect(() => { }, [visibleOrderedShapes]);

  // --- NEW: Memoized Display Items (Handles Stacking) ---
  const displayItems = useMemo(() => {
    const sourceShapes = visibleOrderedShapes;
    if (!stackIdentical) {
      return sourceShapes.map((shape) => {
        const measurementData = getMeasurementDataForShape(shape);

        let pageListString = '';
        if (shape.pageInfo?.name) {
          pageListString = shape.pageInfo.name;
        } else {
          const idx =
            shape.pageInfo?.pageIndex ?? shape.pageInfo?.index;
          if (idx !== undefined && idx !== null) {
            pageListString = `Page ${idx + 1}`;
          }
        }

        return {
          id: shape.id,
          representativeShape: shape,
          shapeIds: [shape.id],
          count: 1,
          totalMeasurementSummary: measurementData.summary,
          pageListString,
        };
      });
    }
    const getShapeStackKey = (shape) => {
      // For POINT shapes, include the marker type to prevent different markers stacking together
      const markerSuffix = shape.type === TOOLS.POINT
        ? `|marker:${shape.pointMarker || 'disk'}`
        : '';
      const isSel = selectedShapeIds.includes(shape.id);
      let key = `${shape.type}|${shape.stroke || 'no-stroke'}${markerSuffix}|sel:${isSel}`;


      // Append page info ONLY if separateByPage is true
      if (separateByPage) {
        const pageId =
          shape?.pageInfo?.name ??
          shape?.pageInfo?.pageIndex ??
          shape?.pageInfo?.index ??
          'unknown-page';
        key += `|page:${pageId}`;
      }

      if (groupingKey) {
        const groupValue =
          groupingKey === GROUP_BY_PAGE_KEY
            ? (shape?.pageInfo?.name ??
              shape?.pageInfo?.pageIndex ??
              shape?.pageInfo?.index ??
              'undefined')
            : (shape?.params?.[groupingKey] ?? 'undefined');
        key += `|${groupingKey}:${groupValue}`;
      }
      return key;
    };
    const stacks = new Map();
    sourceShapes.forEach((shape) => {
      const key = getShapeStackKey(shape);
      if (stacks.has(key)) {
        const existing = stacks.get(key);
        existing.shapeIds.push(shape.id);
        existing.count++;
      } else {
        stacks.set(key, {
          id: `stack-${key}`,
          representativeShape: shape,
          shapeIds: [shape.id],
          count: 1,
        });
      }
    });
    const finalStacks = Array.from(stacks.values());
    finalStacks.forEach((stack) => {
      const representativeMeasurement = getMeasurementDataForShape(
        stack.representativeShape,
      );

      // --- NEW: Calculate Page Info String ---
      const pageNames = new Set();
      stack.shapeIds.forEach((id) => {
        const s = sourceShapes.find((ss) => ss.id === id);
        if (s?.pageInfo) {
          const name = s.pageInfo.name;
          const idx = s.pageInfo.pageIndex ?? s.pageInfo.index;
          if (name) {
            pageNames.add(name);
          } else if (idx !== undefined && idx !== null) {
            pageNames.add(`Page ${idx + 1}`);
          }
        }
      });
      if (pageNames.size === 0) {
        stack.pageListString = '';
      } else {
        // limit to a few if too many? For now just join all.
        stack.pageListString = Array.from(pageNames)
          .sort()
          .join(', ');
      }

      if (stack.count > 1) {
        const repShape = stack?.representativeShape;
        const repBaseScaleProps = repShape?.measurementContext?.currentScaleProps || currentScaleProps;
        const repScaleProps = resolveShapeScaleProps(repShape, repBaseScaleProps, allScales);
        const repRatio = repScaleProps?.ratio;
        const repIsArea = repShape?.type === TOOLS.AREA;
        const repCtx = repShape?.measurementContext;
        const dpiForDisplay = repCtx?.dpi || dpi;
        const imageSizeForDisplay = repCtx?.imageSize || imageSize;
        const stackDisplayUnitSystem = repCtx?.displayUnitSystem || displayUnitSystem;

        let totalInBase = 0;
        let baseUnit = '';
        stack.shapeIds.forEach((id) => {
          const s = sourceShapes.find((ss) => ss.id === id);
          if (s) {
            const data = getMeasurementDataForShape(s);
            if (data.rawValue > 0) {
              const shapeBaseScaleProps = s?.measurementContext?.currentScaleProps || currentScaleProps;
              const shapeScaleProps = resolveShapeScaleProps(s, shapeBaseScaleProps, allScales);
              const shapeRatio = shapeScaleProps?.ratio;
              const linearFactor = (repRatio && shapeRatio) ? shapeRatio / repRatio : 1;
              const conversionFactor = repIsArea ? linearFactor * linearFactor : linearFactor;
              totalInBase += data.rawValue * conversionFactor;
              if (!baseUnit) baseUnit = data.rawUnit;
            }
          }
        });
        const isArea = baseUnit.includes('^2');

        const totalDisplay = isArea
          ? getDisplayAreaMeasurement(
            totalInBase,
            repScaleProps,
            dpiForDisplay,
            stackDisplayUnitSystem,
          )
          : getDisplayMeasurement(
            totalInBase,
            repScaleProps,
            dpiForDisplay,
            imageSizeForDisplay,
            stackDisplayUnitSystem,
          );
        const summaryLabel =
          representativeMeasurement.summary.split(':')[0] ||
          (isArea ? 'Total Area' : 'Total Length');
        stack.totalMeasurementSummary = `${summaryLabel}: ${totalDisplay.value} ${totalDisplay.unit}`;
      } else {
        stack.totalMeasurementSummary =
          representativeMeasurement.summary;
      }
    });
    finalStacks.sort((a, b) => {
      const aSel = a.id.includes('|sel:true');
      const bSel = b.id.includes('|sel:true');
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
    return finalStacks;
  }, [
    visibleOrderedShapes,
    stackIdentical,
    groupingKey,
    selectedShapeIds,
    getMeasurementDataForShape,
    currentScaleProps,
    dpi,
    imageSize,
    separateByPage,
    displayUnitSystem,
  ]); // <-- Added distinctByPage dependency

  const groupedShapesData = useMemo(() => {
    if (!groupingKey) return null;

    const groups = {};
    const sortMeta = new Map(); // groupName -> sortKey

    const getPageIndex = (shape) => {
      const idx =
        shape?.pageInfo?.pageIndex ?? shape?.pageInfo?.index;
      return typeof idx === 'number' ? idx : null;
    };

    const getGroupName = (shape) => {
      if (groupingKey === GROUP_BY_PAGE_KEY) {
        const name = shape?.pageInfo?.name;
        if (name) return name;
        const idx = getPageIndex(shape);
        if (idx !== null) return `Page ${idx + 1}`;
        return t('inventory.not_set');
      }
      return shape?.params?.[groupingKey] || 'Not Set';
    };

    displayItems.forEach((item) => {
      const shape = item.representativeShape;
      const groupName = getGroupName(shape);

      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(item);

      if (!sortMeta.has(groupName)) {
        const sortKey =
          groupingKey === GROUP_BY_PAGE_KEY
            ? (getPageIndex(shape) ?? Number.POSITIVE_INFINITY)
            : String(groupName).toLowerCase();
        sortMeta.set(groupName, sortKey);
      }
    });

    const order = Object.keys(groups).sort((a, b) => {
      // Always put "Not Set" groups at the end
      const isANotSet = a === t('inventory.not_set');
      const isBNotSet = b === t('inventory.not_set');

      if (isANotSet && !isBNotSet) return 1; // A goes after B
      if (!isANotSet && isBNotSet) return -1; // A goes before B
      if (isANotSet && isBNotSet) return 0; // Both are "Not Set", keep original order

      // Handle page grouping (numeric sorting by page index)
      if (groupingKey === GROUP_BY_PAGE_KEY) {
        const sa = sortMeta.get(a);
        const sb = sortMeta.get(b);
        if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb)
          return sa - sb;
      }

      // Default alphabetical/numeric sorting for other groups
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });

    return { groups, order };
  }, [displayItems, groupingKey]);

  const groupedShapes = groupedShapesData?.groups || null;
  const groupedShapeOrder = groupedShapesData?.order || [];

  const allItemIds = useMemo(
    () => displayItems.map((item) => item.id),
    [displayItems],
  );

  // --- Pagination Logic ---
  const totalItems = useMemo(
    () => displayItems.length,
    [displayItems],
  );
  const totalPages = useMemo(
    () => Math.ceil(totalItems / itemsPerPage),
    [totalItems, itemsPerPage],
  );

  // Reset to page 1 if current page exceeds total pages
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedDisplayItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return displayItems.slice(startIndex, endIndex);
  }, [displayItems, currentPage, itemsPerPage]);

  const paginatedItemIds = useMemo(() => {
    if (groupedShapesData) {
      const { groups, order } = groupedShapesData;
      return order.flatMap((groupName) => {
        if (collapsedGroups.has(groupName)) return [];
        const itemsInGroup = groups[groupName] || [];
        // Filter to only show items on the current page
        const paginatedGroupItems = itemsInGroup.filter((item) =>
          paginatedDisplayItems.some((pi) => pi.id === item.id),
        );
        return paginatedGroupItems.map((item) => item.id);
      });
    }
    return paginatedDisplayItems.map((item) => item.id);
  }, [paginatedDisplayItems, groupedShapesData, collapsedGroups]);

  // Pagination handlers
  const goToFirstPage = () => setCurrentPage(1);
  const goToPrevPage = () =>
    setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  // Export current view to CSV - exports what you see with correct measurements
  const handleExportCurrentView = useCallback(() => {
    try {
      // Filter to only selected items
      const itemsToExport = displayItems.filter((item) =>
        item.shapeIds.some((id) => selectedShapeIds.includes(id))
      );

      if (itemsToExport.length === 0) {
        alert(t('inventory.please_select_shapes'));
        return;
      }

      // Get all param keys from selected items
      const allParamKeys = new Set();
      itemsToExport.forEach((item) => {
        const shape = item.representativeShape;
        if (shape.params) {
          Object.keys(shape.params).forEach((key) => {
            // Skip internal keys
            if (!['order', 'tags'].includes(key)) {
              allParamKeys.add(key);
            }
          });
        }
      });

      // Helper to parse measurement string (e.g., "Total Length: 50.00 m" or "Area: 25.5 m²")
      const parseMeasurement = (measurementStr) => {
        if (!measurementStr) return { value: '', unit: '' };
        // Remove label part (e.g., "Total Length:", "Area:", "Net Area:")
        const withoutLabel = measurementStr.replace(/^[^:]+:\s*/, '');

        // Try to match imperial measurements (feet and/or inches): e.g., "9' 11.9"" or "5'" or "120' 4 7/8"" or "11.5""
        if (withoutLabel.includes("'") || withoutLabel.includes('"')) {
          let feet = 0;
          let inches = 0;

          // Match feet part: e.g. 120'
          const feetMatch = withoutLabel.match(/([\d.]+)\s*'/);
          if (feetMatch) {
            feet = parseFloat(feetMatch[1]);
          }

          // Extract inches string (everything after ' if present, or before " if no ')
          let inchesStr = '';
          if (withoutLabel.includes("'")) {
            const parts = withoutLabel.split("'");
            if (parts.length > 1) {
              inchesStr = parts[1].replace(/"/g, '').trim();
            }
          } else {
            inchesStr = withoutLabel.replace(/"/g, '').trim();
          }

          if (inchesStr) {
            // Match fraction: e.g. "4 7/8" or "7/8"
            const fractionMatch = inchesStr.match(/(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)/);
            if (fractionMatch) {
              const whole = fractionMatch[1] ? parseInt(fractionMatch[1], 10) : 0;
              const num = parseInt(fractionMatch[2], 10);
              const den = parseInt(fractionMatch[3], 10);
              inches = whole + (num / den);
            } else {
              // Decimal or integer: e.g. "4.5" or "4"
              inches = parseFloat(inchesStr) || 0;
            }
          }

          const decimalFeet = feet + (inches / 12);
          return {
            value: decimalFeet.toFixed(4), // Decimal feet for calculations
            unit: 'ft'
          };
        }

        // Try to match metric/regular numbers with units: e.g., "50.00 m" or "25.5 m²"
        const metricMatch = withoutLabel.match(/^([\d.,]+)\s*(.*)$/);
        if (metricMatch) {
          return {
            value: metricMatch[1].replace(/,/g, ''), // Remove commas from numbers
            unit: metricMatch[2].trim()
          };
        }

        // Fallback: return as-is
        return { value: withoutLabel, unit: '' };
      };

      // Define columns
      const columns = [
        'Page(s)',
        'Count',
        'Assembly',
        'Measurement Value',
        'Measurement Unit',
        // 'Measurement (Full)', // KEEP FOR DEBUG
        ...Array.from(allParamKeys).sort().filter((key) => !['Total Length', 'Color', 'Assembly', 'Reference', 'Count'].includes(key)),
        'Reference',
        'Version',
        'Type',
        'Color',
        // 'Created By', // Optional: include if you want to track who created the shape
        'Created At',
      ];

      // Helper to escape CSV values (using semicolon delimiter for European format)
      const escapeCSV = (value) => {
        if (value == null || value === undefined) return '';
        const str = String(value);
        // Escape if contains semicolon, quote, or newline
        if (str.includes(';') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Helper to convert decimal separator from period to comma for French Excel
      const convertDecimalToComma = (value) => {
        if (value == null || value === undefined || value === '') return '';
        const str = String(value);
        // Replace periods with commas for decimal numbers
        return str.replace(/(\d)\.(\d)/g, '$1,$2');
      };

      // Build CSV rows (using semicolon delimiter for European format)
      const rows = [columns.map(escapeCSV).join(';')];

      itemsToExport.forEach((item) => {
        const shape = item.representativeShape;

        // Parse measurement into value and unit
        const { value: measurementValue, unit: measurementUnit } =
          parseMeasurement(item.totalMeasurementSummary);

        // Build calculated params (these are not saved to DB but computed)
        const calculatedParams = {
          'Count': item.count
        };

        // Extract the value+unit part (e.g. "50 m" or "10' 5\"") from the summary
        const aggregateValueWithUnit = item.totalMeasurementSummary.replace(/^[^:]+:\s*/, '');

        if (shape.type === TOOLS.POLYLINE) {
          // For lines, use the aggregated total length with unit
          calculatedParams['Total Length'] = aggregateValueWithUnit;
        }
        if (shape.type === TOOLS.AREA) {
          // For polygons/areas, use the aggregated total area with unit
          calculatedParams['Total Area'] = aggregateValueWithUnit;
        }
        if (shape.type === TOOLS.POINT) {
          // For points, use diameter (per shape property)
          const diameter = shape.diameter !== undefined ? shape.diameter : (shape.radius || 0) * 2;
          calculatedParams['Diameter'] = diameter.toFixed(2);
        }

        // Resolve all params (evaluates formulas)
        const resolvedParams = resolveAllParams(
          shape.params || {},
          calculatedParams
        );

        const asmValue = shape.params?.Assembly || '';
        const matchedAssembly = projectAssemblies.find(
          (a) => a.id === asmValue || a.name === asmValue
        );
        const resolvedAssemblyName = matchedAssembly ? (matchedAssembly.name || matchedAssembly.id) : asmValue;

        const rowData = {
          'Count': item.count,
          'Type': shape.type || '',
          'Measurement Value': convertDecimalToComma(measurementValue),
          'Assembly': resolvedAssemblyName,
          'Measurement Unit': measurementUnit,
          'Reference': shape.params?.Reference || '',
          'Color': shape.params?.Color || shape.stroke || '',
          'Page(s)': item.pageListString || '',
          'Version': shape.pageInfo?.version || shape.measurementContext?.version || '',
          'Created By': shape.createdByUsername || '',
          'Created At': shape.createdAt ? new Date(shape.createdAt).toLocaleString() : '',
        };

        // Add resolved (evaluated) custom params
        allParamKeys.forEach((key) => {
          const resolvedValue = resolvedParams[key];

          if (resolvedValue !== undefined && resolvedValue !== null) {
            // Handle mathjs Unit objects
            if (typeof resolvedValue === 'object' && resolvedValue.isUnit) {
              // Format mathjs Unit objects with their unit intact, just like the UI
              const formattedStr = math.format(resolvedValue, { notation: 'fixed', precision: 2 });
              rowData[key] = convertDecimalToComma(formattedStr);
            }
            // Handle error results from formula evaluation
            else if (typeof resolvedValue === 'object' && resolvedValue.error) {
              rowData[key] = resolvedValue.error;
            }
            // Handle regular numbers
            else if (typeof resolvedValue === 'number') {
              rowData[key] = convertDecimalToComma(resolvedValue.toFixed(2));
            }
            // Everything else as string
            else {
              rowData[key] = String(resolvedValue);
            }
          } else {
            rowData[key] = '';
          }
        });

        const values = columns.map((col) => escapeCSV(rowData[col] || ''));
        rows.push(values.join(';'));
      });

      const csvContent = rows.join('\n');

      // Download file
      const timestamp = new Date().toISOString().split('T')[0];
      const projectPrefix = projectName ? `${projectName}_` : '';
      const filename = `${projectPrefix}Takeoff_Selected_${timestamp}.csv`;

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Exported ${itemsToExport.length} selected items to ${filename}`);
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert(t('inventory.export_error', { error: error.message }));
    }
  }, [displayItems, selectedShapeIds, projectName, t, getMeasurementDataForShape]);

  // Track active drag item for overlay
  const [activeDragItem, setActiveDragItem] = useState(null);

  const handleDragStart = (event) => {
    const { active } = event;
    const item = displayItems.find((i) => i.id === active.id);
    if (item) setActiveDragItem(item);
  };

  const handleDragCancel = () => {
    setActiveDragItem(null);
  };

  const handleDragEndWrapper = (event) => {
    handleDragEnd(event);
    setActiveDragItem(null);
  };

  // --- Handlers ---
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Helper: apply new ordered shape IDs to parent or local
    const applyNewOrder = (newOrderShapeIds) => {
      if (typeof onReorderShapes === 'function') {
        onReorderShapes(newOrderShapeIds);
      } else {
        setLocalOrder(newOrderShapeIds);
      }
    };

    // --- CASE 1: No grouping -> flat reorder of displayItems
    if (!groupingKey) {
      const fromIndex = displayItems.findIndex(
        (i) => i.id === active.id,
      );
      const toIndex = displayItems.findIndex((i) => i.id === over.id);
      if (
        fromIndex !== -1 &&
        toIndex !== -1 &&
        fromIndex !== toIndex
      ) {
        const newDisplay = [...displayItems];
        const [moved] = newDisplay.splice(fromIndex, 1);
        newDisplay.splice(toIndex, 0, moved);
        applyNewOrder(newDisplay.flatMap((i) => i.shapeIds));
        return;
      }
    }

    // Utility: find group name for an item id (stack id or shape id)
    const findGroupForItemId = (itemId) => {
      if (!groupedShapes) return null;
      for (const gName in groupedShapes) {
        const items = groupedShapes[gName];
        if (
          items.some(
            (it) => it.id === itemId || it.shapeIds.includes(itemId),
          )
        ) {
          return gName;
        }
      }
      return null;
    };

    // --- CASE 2: Grouped: reorder inside same group
    if (groupingKey && groupedShapes) {
      const activeGroup = findGroupForItemId(active.id);
      const overGroup = findGroupForItemId(over.id);

      if (activeGroup && overGroup && activeGroup === overGroup) {
        const groupItems = groupedShapes[activeGroup];
        const fromIndex = groupItems.findIndex(
          (i) => i.id === active.id,
        );
        const toIndex = groupItems.findIndex((i) => i.id === over.id);
        if (
          fromIndex !== -1 &&
          toIndex !== -1 &&
          fromIndex !== toIndex
        ) {
          const reorderedGroup = [...groupItems];
          const [moved] = reorderedGroup.splice(fromIndex, 1);
          reorderedGroup.splice(toIndex, 0, moved);

          // Reconstruct full display order (groups stay in their original overall order)
          const newDisplayOrder = [];
          Object.keys(groupedShapes)
            .sort()
            .forEach((gName) => {
              if (gName === activeGroup) {
                newDisplayOrder.push(...reorderedGroup);
              } else {
                newDisplayOrder.push(...groupedShapes[gName]);
              }
            });

          applyNewOrder(newDisplayOrder.flatMap((i) => i.shapeIds));
          return;
        }
      }
    }

    // --- CASE 3: Cross-group drop => change group param for ALL shapes in the dragged stack ---
    // Find the displayItem representing the dragged item (stack or single)
    const activeItem = displayItems.find(
      (it) => it.id === active.id || it.shapeIds.includes(active.id),
    );
    const targetShapeIds = activeItem
      ? activeItem.shapeIds
      : [active.id];

    // Normalize active id to actual shape id for lookups
    let activeShapeId = active.id;
    if (
      typeof active.id === 'string' &&
      active.id.startsWith('stack-')
    ) {
      const activeItemFallback = displayItems.find(
        (item) => item.id === active.id,
      );
      if (
        activeItemFallback &&
        activeItemFallback.shapeIds.length > 0
      ) {
        activeShapeId = activeItemFallback.shapeIds[0];
      }
    }

    const activeShape = orderedShapes.find(
      (s) => s.id === activeShapeId,
    );
    let newGroupName = null;

    if (over.data.current?.type === 'group') {
      newGroupName = over.data.current.groupName;
    } else if (groupedShapes) {
      newGroupName = findGroupForItemId(over.id);
    }

    if (activeShape && newGroupName !== null && groupingKey) {
      if (groupingKey === GROUP_BY_PAGE_KEY) return; // Page grouping is read-only
      const currentGroupValue =
        activeShape.params?.[groupingKey] || t('inventory.not_set');
      if (currentGroupValue !== newGroupName) {
        const updatedValue =
          newGroupName === t('inventory.not_set')
            ? null
            : newGroupName;
        // Apply update to every shape id in the dragged stack
        targetShapeIds.forEach((id) => {
          const shapeForId =
            orderedShapes.find((s) => s.id === id) || activeShape;
          const updatedParams = {
            ...(shapeForId.params || {}),
            [groupingKey]: updatedValue,
          };
          onUpdateShape(id, { params: updatedParams });
        });
        // When grouping by Assembly and dropping onto a real assembly group,
        // load the full assembly params onto all shapes in the dragged stack
        if (groupingKey === 'Assembly' && updatedValue) {
          handleLoadAssembly(activeShape, updatedValue, targetShapeIds);
        }
      }
    }
  };

  const toggleGroupCollapse = (groupName) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const toggleCollapseAll = () => {
    // Check if all groups are collapsed
    const allCollapsed =
      groupedShapeOrder.length > 0 &&
      groupedShapeOrder.every((groupName) =>
        collapsedGroups.has(groupName),
      );

    if (allCollapsed) {
      // Expand all
      setCollapsedGroups(new Set());
    } else {
      // Collapse all
      setCollapsedGroups(new Set(groupedShapeOrder));
    }
  };

  const toggleExpanded = (shapeId) => {
    const newExpanded = new Set(expandedShapes);
    if (newExpanded.has(shapeId)) {
      newExpanded.delete(shapeId);
    } else {
      newExpanded.add(shapeId);
    }
    setExpandedShapes(newExpanded);
  };

  const handleSaveAsAssembly = async (shapeOrAssembly) => {
    if (!authUser || !firebase) {
      console.error('Not authenticated or Firebase not initialized.');
      return;
    }

    const organization = shapes[0]?.pageInfo?.organization || organizationFromShapes;
    if (!organization) {
      console.error('No organization found, cannot save assembly');
      return;
    }

    // When called from AssemblyManager the argument is the full assembly object
    // {id, name, params} or {id, name, objects:[{params}]}. Detect this case
    // to skip shape-specific logic.
    const isAssemblyObject =
      shapeOrAssembly?.id &&
      !shapeOrAssembly?.type &&
      !shapeOrAssembly?.pageInfo;

    if (isAssemblyObject) {
      // Assembly-object path — handle ASSEMBLY_ALREADY_EXISTS with prompt to update
      let assemblyParams = shapeOrAssembly.params;
      if (!assemblyParams || Object.keys(assemblyParams).length === 0) {
        if (Array.isArray(shapeOrAssembly.objects) && shapeOrAssembly.objects.length > 0) {
          assemblyParams = shapeOrAssembly.objects[0]?.params || {};
        } else {
          assemblyParams = {};
        }
      }
      setSaveStatus('saving');
      try {
        await firebase.doCreateAssembly(
          organization,
          projectName,
          shapeOrAssembly.id,
          assemblyParams,
          shapeOrAssembly.name,
        );
        setSaveStatus('saved');
      } catch (error) {
        if (error.code === 'ASSEMBLY_ALREADY_EXISTS') {
          const shouldUpdate = window.confirm(
            `Assembly "${shapeOrAssembly.id}" already exists. Do you want to overwrite it?`,
          );
          if (shouldUpdate) {
            try {
              await firebase.doUpdateAssembly(
                organization,
                projectName,
                shapeOrAssembly.id,
                assemblyParams,
                shapeOrAssembly.name,
              );
              setSaveStatus('saved');
            } catch (updateError) {
              console.error('Error updating assembly:', updateError);
              setSaveStatus('error');
            }
          } else {
            setSaveStatus('idle');
          }
        } else {
          console.error('Error saving assembly:', error);
          setSaveStatus('error');
        }
      } finally {
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
      return;
    }

    // Shape-based save: use shared helper
    const shape = shapeOrAssembly;
    const assemblyName = shape.params?.Assembly;
    if (!assemblyName) {
      console.warn('Shape has no Assembly param set — cannot save assembly');
      return;
    }

    setSaveStatus('saving');

    const result = await saveAssemblyFromShape({
      firebase,
      organization,
      projectName,
      shape: { ...shape, params: { ...(shape.params || {}), Assembly: assemblyName } },
      getMeasurementDataForShape,
    });

    if (result.success) {
      setSaveStatus('saved');
      console.log(`Assembly "${assemblyName}" saved successfully!`);
    } else if (result.status === 'cancelled') {
      setSaveStatus('idle');
    } else {
      setSaveStatus('error');
    }

    setTimeout(() => setSaveStatus('idle'), result.status === 'cancelled' ? 0 : 2000);
  };

  const handleLoadAssembly = useCallback(
    async (shape, assemblyName, targetShapeIds = [shape.id]) => {
      if (!authUser || !firebase) {
        console.error(
          'Not authenticated or Firebase not initialized.',
        );
        return;
      }

      if (!assemblyName) {
        alert(t('inventory.assembly_name_required'));
        return;
      }

      // Get organization from shape's pageInfo, or from shapes array as fallback
      const organization =
        shape.pageInfo?.organization ||
        shapes[0]?.pageInfo?.organization;
      if (!organization) {
        console.error(
          'No organization found in shape or shapes array, cannot load assembly',
        );
        alert(t('inventory.cannot_load_assembly'));
        return;
      }

      setLoadStatus('loading');

      try {
        // Resolve friendly assembly name to actual document ID if possible
        const matchedAssembly = projectAssemblies.find(
          (a) => a.name === assemblyName || a.id === assemblyName
        );
        const resolvedId = matchedAssembly ? matchedAssembly.id : assemblyName;

        const assemblyDoc = await firebase
          .assemblyDoc(organization, projectName, resolvedId)
          .get();

        if (!assemblyDoc.exists) {
          alert(
            t('inventory.assembly_not_found', { name: assemblyName }),
          );
          setLoadStatus('error');
          return;
        }

        const assemblyData = assemblyDoc.data();

        // Filter out calculated params from the assembly data (they will be recomputed per shape)
        // Update each target shape's parameters with the loaded assembly data
        const updatedShapes = workingShapes.map((shape) => {
          if (targetShapeIds.includes(shape.id)) {
            // Filter out calculated params from the assembly data
            const assemblyParams = assemblyData.params || {};
            const filteredAssemblyParams = Object.keys(
              assemblyParams,
            ).reduce((acc, key) => {
              if (!CALCULATED_PARAM_KEYS.includes(key)) {
                acc[key] = assemblyParams[key];
              }
              return acc;
            }, {});

            const updatedParams = {
              Assembly: resolvedId,
              ...(shape.params?.Reference !== undefined ? { Reference: shape.params.Reference } : {}),
              ...(shape.params?.Color !== undefined ? { Color: shape.params.Color } : {}),
              ...filteredAssemblyParams,
            };

            // Notify parent of individual updates
            if (onUpdateShape) {
              onUpdateShape(shape.id, { params: updatedParams });
            }

            return { ...shape, params: updatedParams };
          }
          return shape;
        });

        // Track all modified shapes
        setModifiedShapeIds((prev) => {
          const newSet = new Set(prev);
          targetShapeIds.forEach((id) => newSet.add(id));
          return newSet;
        });

        setInternalShapes(updatedShapes);
        setLoadStatus('loaded');
        console.log(
          `Assembly "${assemblyName}" loaded successfully!`,
        );
      } catch (error) {
        console.error('Error loading assembly:', error);
        setLoadStatus('error');
        alert(
          t('inventory.failed_load_assembly', { msg: error.message }),
        );
      } finally {
        setTimeout(() => setLoadStatus('idle'), 2000); // Reset after 2 seconds
      }
    },
    [authUser, firebase, projectName, onUpdateShape, workingShapes, projectAssemblies],
  );

  // Handler to add a new parameter to all shapes in a group
  const handleAddParamToGroup = useCallback(
    (shapeIds, key, value) => {
      if (!key || !value) return;

      const updatedShapes = workingShapes.map((shape) => {
        if (shapeIds.includes(shape.id)) {
          const existingParams = shape.params || {};
          const existingOrder = Array.isArray(existingParams.order)
            ? [...existingParams.order]
            : [];

          // Add key to order if not already present
          if (!existingOrder.includes(key)) {
            existingOrder.push(key);
          }

          const updatedParams = {
            ...existingParams,
            [key]: value,
            order: existingOrder,
          };

          // Notify parent of individual updates
          if (onUpdateShape) {
            onUpdateShape(shape.id, { params: updatedParams });
          }

          return { ...shape, params: updatedParams };
        }
        return shape;
      });

      // Track all modified shapes
      setModifiedShapeIds((prev) => {
        const newSet = new Set(prev);
        shapeIds.forEach((id) => newSet.add(id));
        return newSet;
      });

      setInternalShapes(updatedShapes);
    },
    [onUpdateShape, workingShapes, setInternalShapes],
  );

  // Wrapper for onUpdateShape that updates internal state and notifies parent
  const handleUpdateShapeInternal = useCallback(
    (shapeId, updates) => {
      // Track this shape as modified
      setModifiedShapeIds((prev) => {
        if (prev.has(shapeId)) return prev; // Already modified
        const newSet = new Set(prev);
        newSet.add(shapeId);
        return newSet;
      });

      // Update shapes
      const updatedShapes = workingShapes.map((shape) =>
        shape.id === shapeId ? { ...shape, ...updates } : shape,
      );
      setInternalShapes(updatedShapes);

      // Notify parent
      if (onUpdateShape) {
        onUpdateShape(shapeId, updates);
      }
    },
    [workingShapes, setInternalShapes, onUpdateShape],
  );

  // --- MODIFIED: renderShapeList now iterates over `displayItems` ---
  const renderShapeList = (itemsToRender) => {
    return itemsToRender.map((item) => {
      if (item._separator) {
        return <div key="__sel-separator__" className="horizontal-separator shapelist-separator-centered" />;
      }
      const {
        representativeShape,
        count,
        shapeIds,
        totalMeasurementSummary,
        pageListString,
      } = item;
      // Determine selection state for the item/stack
      const selectedCount = shapeIds.filter((id) =>
        selectedShapeIds.includes(id),
      ).length;
      const isSelected = selectedCount > 0;
      const isIndeterminate =
        selectedCount > 0 && selectedCount < count;

      const effectiveTargetShapeIds =
        isIndeterminate
          ? shapeIds.filter((id) => selectedShapeIds.includes(id))
          : shapeIds;

      return (
        <DraggableShapeListItem_takeoff
          projectAssemblies={projectAssemblies}
          firebase={firebase}
          key={item.id}
          id={item.id} // Pass id for dnd-kit
          shapeIds={shapeIds} // <-- pass the underlying shape IDs so DroppableGroup can select them
          shape={representativeShape} // The shape to display info for
          count={count} // The number of items in the stack
          measurementData={{
            ...getMeasurementDataForShape(representativeShape),
            summary: totalMeasurementSummary,
          }} // Pass the new total summary
          isSelected={isSelected}
          isIndeterminate={isIndeterminate}
          isExpanded={expandedShapes.has(item.id)}
          activeDetailView={activeDetailView}
          onShapeSelect={() => {
            // If not all are selected, select all. Otherwise, deselect all.
            const shouldSelectAll = selectedCount < count;
            onShapeSelect(shapeIds, shouldSelectAll);
          }}
          onToggleExpand={() =>
            toggleExpanded(item.id)
          }
          onUpdateShape={handleUpdateShapeInternal} // Use wrapped version that updates history
          onSaveAsAssembly={() =>
            handleSaveAsAssembly(representativeShape)
          }
          onLoadAssembly={(assemblyName) =>
            handleLoadAssembly(
              representativeShape,
              assemblyName || representativeShape.params?.Assembly,
              effectiveTargetShapeIds,
            )
          }
          saveStatus={saveStatus}
          loadStatus={loadStatus}
          stackIdentical={stackIdentical} // <-- tell item whether stacking is enabled
          groupingKey={groupingKey} // <-- Pass groupingKey prop
          pageListString={pageListString} // <-- Pass page info string
        />
      );
    });
  };

  return (
    <div className="ShapeList Shapetakeoff-container">
      <>
        <div className="pdftoolbar" style={{ marginTop: '0px' }}>
          {/* --- Detail View Selector (currently keep it commented out) --- */}
          {/* keep for future use if we re-enable detail views */}
          {/* <select
          className="detail-view-controls"
          value={activeDetailView}
          onChange={(e) => setActiveDetailView(e.target.value)}
          title={t('inventory.detail_view_title')}
        >
          <option value="measurements">{t('inventory.detail_view_measures')}</option>
          <option value="tags">{t('inventory.detail_view_tags')}</option>
          <option value="params">{t('inventory.detail_view_params')}</option>
          <option value="vertices">{t('inventory.detail_view_vertices')}</option>
        </select> */}

          {/* --- Global Select All Checkbox --- */}
          <input
            ref={globalCheckboxRef}
            type="checkbox"
            className="select-all-checkbox"
            checked={isGlobalAllSelected}
            onChange={handleGlobalSelect}
            title={t('inventory.select_all_tooltip')}
          />

          {/* --- Collapse/Expand All Button --- */}
          {groupedShapeOrder.length > 0 && (
            <button
              onClick={toggleCollapseAll}
              className="collapse-all-button"
              title={
                groupedShapeOrder.every((groupName) =>
                  collapsedGroups.has(groupName),
                )
                  ? t('inventory.expand_all_tooltip')
                  : t('inventory.collapse_all_tooltip')
              }
            >
              {groupedShapeOrder.every((groupName) =>
                collapsedGroups.has(groupName),
              )
                ? '➕'
                : '➖'}
            </button>
          )}
          {(allParamKeys.length > 0 || hasPageInfo) && (
            <select
              className="shapelist-detail-view-controls"
              // style={{ maxWidth: '140px' }}
              value={groupingKey}
              onChange={(e) => setGroupingKey(e.target.value)}
              title={t('inventory.group_by_param_title')}
            >
              <option value="">{t('inventory.no_grouping')}</option>
              {hasPageInfo && (
                <option value={GROUP_BY_PAGE_KEY}>
                  {t('inventory.by_label', {
                    label: getGroupingLabel(GROUP_BY_PAGE_KEY, t),
                  })}
                </option>
              )}
              {['Assembly', 'Color', 'Reference', 'type'].map(
                (key) => (
                  <option key={key} value={key}>
                    {t('inventory.by_label', {
                      label: getGroupingLabel(key, t),
                    })}
                  </option>
                ),
              )}
              {/*will review the pertinence of having this many allparamkeys*/}
              {/* {allParamKeys.map(key => (
              <option key={key} value={key}>by: {key}</option>
            // ))} */}
            </select>
          )}

          {/* --- Stacking Checkbox --- */}
          <input
            className="shapelist-detail-view-assembly"
            type="checkbox"
            checked={stackIdentical}
            onChange={(e) => setStackIdentical(e.target.checked)}
            title={t('inventory.stack_elements_tooltip')}
          />
          {/* --- Separate by Page Checkbox (Renamed from Selected Only) --- */}
          <input
            className="shapelist-detail-view-selected-only"
            type="checkbox"
            checked={separateByPage}
            onChange={(e) => setSeparateByPage(e.target.checked)}
            title={t('inventory.separate_by_page_title')}
          />
          <button
            onClick={onOpenAssemblyManager}
            className="assembly-manager-button"
            title={`${t('shapeList.assembly_manager_title')}\n${t('shapeList.assembly_manager_title_desc')}`}
          >
            <SettingsIcon className="tool-icon stroke-icon" />
          </button>
          {/* --- Export Button --- */}
          <button
            onClick={handleExportCurrentView}
            className="export-button"
            title={t('inventory.export_selected_to_csv')}
            disabled={selectedShapeIds.length === 0}
          >
            <ScvIcon className="tool-icon" />
          </button>
          {/* --- Version Selector --- */}
          {versions.length > 0 && (
            <div
              className="takeoff-version-selector"
              style={{ marginLeft: 'auto' }}
            >
              {projectName && (
                <span className="shapelist-stats">
                  {selectedShapeIds.length}{' '}
                  {t('inventory.selected_count')} | {shapes.length}{' '}
                  {t('inventory.total_count')}
                  {isPending && ` (${t('inventory.updating')})`}
                </span>
              )}
              <button
                type="button"
                ref={versionTriggerRef}
                onClick={handleToggleVersionDropdown}
                className="dropdown-trigger"
                title={t('replan.pdfControls.select_versions')}
              >
                {selectedVersions.length === 0
                  ? t('inventory.select_a_version')
                  : selectedVersions.length === 1
                    ? selectedVersions[0]
                    : `${selectedVersions.length} ${t('inventory.versions_count')}`}
                {loadingVersionShapes ? ' ⏳' : ' ▾'}
              </button>
              {isVersionDropdownOpen &&
                ReactDOM.createPortal(
                  <div
                    ref={versionDropdownMenuRef}
                    className="takeoff-version-dropdown"
                    style={{
                      position: 'fixed',
                      top: versionDropdownPos.top,
                      left: versionDropdownPos.left,
                    }}
                  >
                    <div className="version-actions">
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() => onVersionsChange?.(versions)}
                      >
                        {t('replan.pdfControls.all')}
                      </button>
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() => onVersionsChange?.([])}
                      >
                        {t('replan.pdfControls.none')}
                      </button>
                      <button
                        type="button"
                        className="tiny-button"
                        onClick={() =>
                          setIsVersionDropdownOpen(false)
                        }
                      >
                        ❌
                      </button>
                    </div>
                    <div className="version-options">
                      {versions.map((version) => (
                        <label
                          key={version}
                          className="version-option"
                        >
                          <input
                            type="checkbox"
                            checked={selectedVersions.includes(
                              version,
                            )}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...selectedVersions, version]
                                : selectedVersions.filter(
                                  (v) => v !== version,
                                );
                              onVersionsChange?.(next);
                            }}
                          />
                          {version}
                        </label>
                      ))}
                    </div>
                  </div>,
                  document.body,
                )}
            </div>
          )}
          {/* --- Undo/Redo/Save Controls --- */}
          <div className="shapelist-history-controls">
            <button
              onClick={undo}
              disabled={!canUndo}
              title={t('inventory.undo_tooltip')}
            >
              ↶
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              title={t('inventory.redo_tooltip')}
            >
              ↷
            </button>
            <button
              onClick={handleSave}
              title={`${t('inventory.save_changes_shortcut')}${modifiedShapeIds.size > 0 ? ` - ${modifiedShapeIds.size} ${t('inventory.modified')}` : ''}`}
              className="save-btn"
              disabled={
                saveStatus === 'saving' || modifiedShapeIds.size === 0
              }
            >
              {saveStatus === 'saving'
                ? '⏳'
                : saveStatus === 'saved'
                  ? '👍'
                  : '💾'}{' '}
              {modifiedShapeIds.size > 0
                ? ` (${modifiedShapeIds.size})`
                : ''}
            </button>
          </div>
        </div>

        {/* --- Pagination Controls --- */}
        {/* the pagination controls are shown only if totalItems > 50 */}
        <div style={{ marginTop: 'calc(var(--pico-spacing)*2.5)' }}></div>
        {totalItems > ITEMS_PER_PAGE_OPTIONS[0] && (
          <div className="shapelist-pagination">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              title={t('inventory.first_page_title')}
            >
              ⏮
            </button>
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 1}
              title={t('inventory.previous_page_title')}
            >
              ◀
            </button>
            <span className="pagination-info-text">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              title={t('inventory.next_page_title')}
            >
              ▶
            </button>
            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              title={t('inventory.last_page_title')}
            >
              ⏭
            </button>
            <select
              className="items-per-page-select"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              title={t('inventory.items_per_page_title')}
            >
              {ITEMS_PER_PAGE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <span className="items-count-span">
              ({totalItems} items)
            </span>
          </div>
        )}
      </>
      <div className="scrollable-shape-container">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEndWrapper}
        >
          <SortableContext
            items={paginatedItemIds}
            strategy={verticalListSortingStrategy}
          >
            {groupedShapesData
              ? groupedShapeOrder.map((groupName) => {
                const itemsInGroup = groupedShapes[groupName] || [];
                // Filter to only show items on the current page
                const paginatedGroupItems = itemsInGroup.filter(
                  (item) =>
                    paginatedDisplayItems.some(
                      (pi) => pi.id === item.id,
                    ),
                );
                if (paginatedGroupItems.length === 0) return null;

                // Find assembly data when grouping by Assembly
                const isAssemblyGrouping =
                  groupingKey === 'Assembly';
                const assemblyData = isAssemblyGrouping
                  ? projectAssemblies.find(
                    (a) =>
                      a.id === groupName || a.name === groupName,
                  )
                  : null;
                return (
                  <DroppableGroup_takeoff
                    key={groupName}
                    groupName={groupName}
                    groupingKey={groupingKey}
                    groupingKeyLabel={getGroupingLabel(
                      groupingKey,
                      t,
                    )}
                    groupShapeIds={paginatedGroupItems.flatMap(
                      (item) => item.shapeIds,
                    )}
                    itemCount={paginatedGroupItems.reduce(
                      (sum, item) => sum + item.count,
                      0,
                    )}
                    isCollapsed={collapsedGroups.has(groupName)}
                    onToggleCollapse={() =>
                      toggleGroupCollapse(groupName)
                    }
                    selectedShapeIds={selectedShapeIds}
                    onShapeSelect={onShapeSelect}
                    isAssemblyGrouping={isAssemblyGrouping}
                    assemblyData={assemblyData}
                    onAddParam={handleAddParamToGroup}
                    onApplyAssembly={(assemblyName, shapeIds) => {
                      const repShape = orderedShapes.find((s) => shapeIds.includes(s.id));
                      if (repShape) handleLoadAssembly(repShape, assemblyName, shapeIds);
                    }}
                  >
                    {/* The SortableContext is now outside, so we just render the items */}
                    {!collapsedGroups.has(groupName) &&
                      renderShapeList(paginatedGroupItems)}
                  </DroppableGroup_takeoff>
                );
              })
              : renderShapeList(paginatedDisplayItems)}
          </SortableContext>
          <DragOverlay dropAnimation={null}>
            {activeDragItem && (
              <div className="drag-overlay-preview">
                {/*
                Re-use the visual part only (not DraggableShapeListItem because that re-registers useSortable).
                Minimal clone:
              */}
                {renderShapeList([activeDragItem])}
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
      {assemblyManagerOpen && (
        <div
          className="assembly-modal-overlay"
          onClick={() => setAssemblyManagerOpen(false)}
        >
          <div
            className="assembly-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <AssemblyManager
              firebase={firebase}
              projectName={projectName}
              organizationId={shapes[0]?.pageInfo?.organization || organizationFromShapes}
              authUser={authUser}
              onAssembliesChange={setProjectAssemblies}
              onClose={() => setAssemblyManagerOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(ShapeList_takeoff);
